<template>
  <div class="circuit-equivalent-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$R_eq = R₁ + R₂ | 1/R_eq = 1/R₁ + 1/R₂$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 电路渲染舞台 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="240" viewBox="0 0 500 240">
          <defs>
            <filter id="glow">
               <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
               <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>

          <!-- 电源 -->
          <rect x="20" y="110" width="10" height="30" fill="#facc15" />
          <line x1="30" y1="125" x2="60" y2="125" stroke="#475569" stroke-width="2" />
          <line x1="440" y1="125" x2="470" y2="125" stroke="#475569" stroke-width="2" />
          <rect x="470" y="110" width="10" height="30" fill="#facc15" />

          <!-- 等效变换动画逻辑 -->
          <g v-if="step === 0">
             <!-- 原始电路: 混联 -->
             <line x1="60" y1="125" x2="100" y2="125" stroke="#94a3b8" stroke-width="2" />
             <!-- R1 & R2 串联支路 -->
             <rect x="120" y="70" width="50" height="20" fill="#0ea5e9" rx="2" />
             <text x="145" y="84" text-anchor="middle" fill="#fff" font-size="10">R1: 10Ω</text>
             <line x1="170" y1="80" x2="210" y2="80" stroke="#94a3b8" />
             <rect x="210" y="70" width="50" height="20" fill="#0ea5e9" rx="2" />
             <text x="235" y="84" text-anchor="middle" fill="#fff" font-size="10">R2: 20Ω</text>
             
             <!-- R3 并联支路 -->
             <rect x="165" y="160" width="50" height="20" fill="#10b981" rx="2" />
             <text x="190" y="174" text-anchor="middle" fill="#fff" font-size="10">R3: 30Ω</text>

             <!-- 连线 -->
             <path d="M 100 125 L 100 80 L 120 80 M 260 80 L 300 80 L 300 125" fill="none" stroke="#475569" />
             <path d="M 100 125 L 100 170 L 165 170 M 215 170 L 300 170 L 300 125" fill="none" stroke="#475569" />
             <line x1="300" y1="125" x2="440" y2="125" stroke="#475569" stroke-width="2" />
          </g>

          <g v-if="step === 1">
             <!-- 第一步：合并串联 -->
             <line x1="60" y1="125" x2="100" y2="125" stroke="#94a3b8" stroke-width="2" />
             <rect x="150" y="70" width="80" height="20" fill="#0369a1" rx="2" filter="url(#glow)" />
             <text x="190" y="84" text-anchor="middle" fill="#fff" font-size="10">R12: 30Ω (串联和)</text>
             
             <rect x="150" y="160" width="80" height="20" fill="#10b981" rx="2" />
             <text x="190" y="174" text-anchor="middle" fill="#fff" font-size="10">R3: 30Ω</text>

             <path d="M 100 125 L 100 80 L 150 80 M 230 80 L 280 80 L 280 125" fill="none" stroke="#475569" />
             <path d="M 100 125 L 100 170 L 150 170 M 230 170 L 280 170 L 280 125" fill="none" stroke="#475569" />
             <line x1="280" y1="125" x2="440" y2="125" stroke="#475569" stroke-width="2" />
          </g>

          <g v-if="step === 2">
             <!-- 第二步：合并并联 (最终等效) -->
             <line x1="60" y1="125" x2="180" y2="125" stroke="#94a3b8" stroke-width="2" />
             <rect x="180" y="110" width="120" height="30" fill="#f59e0b" rx="4" filter="url(#glow)" />
             <text x="240" y="129" text-anchor="middle" fill="#fff" font-size="12" font-weight="bold">Req = 15Ω (等效电阻)</text>
             <line x1="300" y1="125" x2="440" y2="125" stroke="#94a3b8" stroke-width="2" />
          </g>

          <!-- 电子流动动画 (仅在非静止时) -->
          <circle v-for="i in 5" :key="i" r="2" fill="#facc15" opacity="0.6">
             <animateMotion v-if="step < 2" :dur="3-i*0.2" repeatCount="indefinite" path="M 60 125 L 100 125" />
             <animateMotion v-else :dur="1" repeatCount="indefinite" path="M 60 125 L 440 125" />
          </circle>
        </svg>

        <div class="hud-center">
           <div class="step-indicator">
              当前状态：{{ stepLabels[step] }}
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
         <div class="control-card">
            <div class="label-row">
               请选择变换阶段：由繁入简
            </div>
            <div class="btn-group">
               <el-button size="small" :type="step === 0 ? 'primary' : 'info'" @click="step = 0">1. 原始混联电路</el-button>
               <el-button size="small" :type="step === 1 ? 'primary' : 'info'" @click="step = 1">2. 串联支路合并</el-button>
               <el-button size="small" :type="step === 2 ? 'primary' : 'info'" @click="step = 2">3. 整体等效化简</el-button>
            </div>
         </div>

         <div class="discovery-card-compact" v-latex>
            <i class="el-icon-magic-stick"></i>
            <p v-if="step === 0"><strong>混合电路：</strong>电路由 R1, R2 串联后与 R3 并联组成。我们要通过“黑盒”思想分步骤将其简化。</p>
            <p v-if="step === 1"><strong>局部等效：</strong>根据串联规律 R1+R2=30Ω。此时原来的两个电阻被一个等效电阻 R12 取代，电路结构变清晰了。</p>
            <p v-if="step === 2"><strong>整体等效：</strong>根据并联公式 1/Req = 1/R12 + 1/R3，求得 Req=15Ω。整个复杂网络最终变成了一个理想电阻。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CircuitEquivalentViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      step: 0,
      stepLabels: ['混联结构 (原始)', '分步简化 (串联求和)', '全等效电路 (最终等效)']
    }
  },
  computed: {
    displayTitle() {
      return this.knowledgePoint || '电路的等效变换：化繁为简的艺术';
    }
  }
}
</script>

<style scoped lang="scss">
.circuit-equivalent-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 0.85rem; color: #1e293b; font-weight: 500; } .formula-chip { background: #fef3c7; color: #92400e; padding: 4px 12px; border-radius: 20px; font-weight: 700; font-family: monospace; font-size: 0.75rem; } }
.simulation-stage { flex: 1; background: #0f172a; border-radius: 12px; position: relative; display: flex; align-items: center; justify-content: center; border: 1.5px solid #1e293b; }
.hud-center { position: absolute; top: 15px; left: 50%; transform: translateX(-50%); 
  .step-indicator { background: rgba(15, 23, 42, 0.8); color: #0ea5e9; border: 1px solid #334155; padding: 6px 15px; border-radius: 30px; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; }
}
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #fff; padding: 12px; border-radius: 12px; border: 1px solid #e2e8f0; .label-row { font-size: 11px; color: #64748b; margin-bottom: 8px; font-weight: bold; } .btn-group { display: flex; gap: 8px; } }
.discovery-card-compact { background: #f8fafc; padding: 10px 15px; border-radius: 12px; display: flex; align-items: flex-start; gap: 10px; border: 1px solid #e2e8f0; i { color: #f59e0b; margin-top: 2px; } p { margin: 0; font-size: 0.75rem; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
